import { usePromisesQueue } from "@tecnobit-srl/hook-use-promises-queue";
import { useRouteContextHeaders } from "@tecnobit-srl/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
//#region src/request-signal.ts
var composeRequestSignal = (internalSignal, callerSignal) => callerSignal ? AbortSignal.any([internalSignal, callerSignal]) : internalSignal;
//#endregion
//#region src/use-fetch.ts
var parseParams = (params) => {
	if (!params) return {};
	return Object.keys(params).reduce((obj, paramName) => {
		return {
			...obj,
			[paramName]: params[paramName].toString()
		};
	}, {});
};
function useFetch({ url, init, params, initialData, parseResponse, async = false }) {
	const [data, setData] = useState(initialData);
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const { appendQueue } = usePromisesQueue();
	const mergeRouteContextHeaders = useRouteContextHeaders();
	const controllerRef = useRef(void 0);
	const abort = useCallback(() => {
		controllerRef.current?.abort();
	}, []);
	const _fetch = useCallback(async () => {
		if (!url) return;
		setLoading(true);
		setError("");
		const controller = new AbortController();
		controllerRef.current = controller;
		try {
			const _url = typeof window === "undefined" ? new URL(url) : new URL(url, window.location.href);
			new URLSearchParams(parseParams(params)).forEach((value, key) => {
				_url.searchParams.append(key, value);
			});
			const signal = composeRequestSignal(controller.signal, init?.signal);
			const res = await fetch(_url, {
				...init,
				headers: mergeRouteContextHeaders(_url, init?.headers),
				signal
			});
			if (!res.ok) throw new Error("Request failed");
			const response = await res.json();
			if (!parseResponse) setData(response.data);
			else setData(parseResponse(response));
		} catch (err) {
			setError(err instanceof Error ? err.toString() : String(err));
		} finally {
			if (controllerRef.current === controller) controllerRef.current = void 0;
			setLoading(false);
		}
	}, [
		init,
		mergeRouteContextHeaders,
		params,
		parseResponse,
		url
	]);
	const fetchFn = useCallback(() => {
		appendQueue(_fetch);
	}, [_fetch, appendQueue]);
	useEffect(() => {
		if (!data && !async) fetchFn();
		return () => {
			abort();
		};
	}, []);
	return useMemo(() => ({
		data,
		loading,
		error,
		abort,
		fetchFn
	}), [
		data,
		loading,
		error,
		abort,
		fetchFn
	]);
}
//#endregion
export { useFetch };
